import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  PhoneCall, CheckCircle2, AlertTriangle, HelpCircle, Phone, Mail, Clock, MessageSquare,
} from 'lucide-react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import ActivityDetailDialog from '@/components/ActivityDetailDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso.slice(0, 16).replace('T', ' ');
  }
};

const SCENARIO_LABEL = {
  bienvenue_google: 'Bienvenue', bienvenue: 'Bienvenue',
  bilan: 'Bilan', reactivation: 'Réactivation',
};
const scenarioLabel = (s) => SCENARIO_LABEL[(s || '').toLowerCase()] || s || '—';

// reason → readable badge. Green = interested, red = unhappy, orange = to check.
const REASON_META = {
  Rappel: { label: 'Mécontent', cls: 'bg-red-500/10 text-red-600 border-red-500/40', Icon: AlertTriangle },
  Unknown: { label: 'Mécontent', cls: 'bg-red-500/10 text-red-600 border-red-500/40', Icon: AlertTriangle },
  interesse_bilan: { label: 'Intéressé — Bilan', cls: 'bg-green-500/10 text-green-600 border-green-500/40', Icon: CheckCircle2 },
  interesse_reactivation: { label: 'Intéressé — Réactivation', cls: 'bg-green-500/10 text-green-600 border-green-500/40', Icon: CheckCircle2 },
  a_verifier: { label: 'À vérifier', cls: 'bg-amber-500/10 text-amber-600 border-amber-500/40', Icon: HelpCircle },
  injoignable: { label: 'Injoignable', cls: 'bg-muted text-muted-foreground border-border', Icon: Phone },
};

const ReasonBadge = ({ reason }) => {
  const m = REASON_META[reason];
  if (!m) return <Badge variant="secondary">{reason || '—'}</Badge>;
  const { label, cls, Icon } = m;
  return (
    <Badge variant="outline" className={`gap-1 ${cls}`}>
      <Icon size={12} /> {label}
    </Badge>
  );
};

// The action-critical info: WHEN to call back. Made deliberately prominent.
const PreferenceTag = ({ pref }) => {
  const lower = (pref || '').toLowerCase();
  let label = pref || 'Indifférent';
  if (lower.includes('matin')) label = 'Matin';
  else if (lower.includes('après') || lower.includes('apres') || lower.includes('midi')) label = 'Après-midi';
  else if (lower.includes('soir')) label = 'Soir';
  else if (!pref || lower.includes('indiff') || lower === 'unknown') label = 'Indifférent';
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-sm font-semibold">
      <Clock size={14} /> {label}
    </span>
  );
};

const cbToEntry = (cb) => ({
  action_type: 'ai_call',
  display_status: 'call_sent',
  date: cb.received_at || cb.created_at,
  client_email: cb.client_email,
  client_name: cb.client_name,
  scenario: scenarioLabel(cb.scenario),
  call_status: 'Completed',
  transcript: cb.transcript,
  recording_url: cb.recording_url,
  outcome: cb.summary,
  sentiment: cb.sentiment,
  type_probleme: cb.type_probleme,
  besoin_detecte: cb.besoin_detecte,
  raison_depart: cb.raison_depart,
  preference_rappel: cb.preference_rappel,
});

const Callbacks = () => {
  const [filter, setFilter] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/callbacks`, { params: { status: filter } });
      setItems(res.data || []);
    } catch (e) {
      toast.error('Erreur de chargement des rappels');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const markDone = async (id) => {
    try {
      await axios.patch(`${API}/callbacks/${id}`, { status: 'done' });
      toast.success('Rappel marqué comme traité');
      fetchList();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const ContactLinks = ({ cb }) => (
    <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-2 mt-0.5">
      {cb.client_phone && (
        <a href={`tel:${cb.client_phone}`} className="hover:underline flex items-center gap-1">
          <Phone size={11} /> {cb.client_phone}
        </a>
      )}
      {cb.client_email && (
        <a href={`mailto:${cb.client_email}`} className="hover:underline flex items-center gap-1">
          <Mail size={11} /> {cb.client_email}
        </a>
      )}
    </div>
  );

  const Actions = ({ cb, full }) => (
    <div className={`flex gap-2 ${full ? 'w-full' : 'justify-end'}`}>
      <Button
        size="sm" variant="outline"
        className={full ? 'flex-1' : ''}
        onClick={() => setSelectedEntry(cbToEntry(cb))}
        data-testid={`view-convo-${cb.id}`}
      >
        <MessageSquare size={14} className="mr-1" /> Voir la conversation
      </Button>
      {cb.status === 'pending' ? (
        <Button
          size="sm" className={full ? 'flex-1' : ''}
          onClick={() => markDone(cb.id)}
          data-testid={`mark-done-${cb.id}`}
        >
          <CheckCircle2 size={14} className="mr-1" /> Marquer traité
        </Button>
      ) : (
        <Badge variant="secondary">Traité</Badge>
      )}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="space-y-6"
    >
      <div>
        <h1 className="text-3xl font-heading flex items-center gap-2">
          <PhoneCall className="text-primary" size={28} />
          Clients à rappeler
        </h1>
        <p className="text-muted-foreground mt-1.5 leading-relaxed">
          Tous les clients identifiés par l'IA Callkom comme nécessitant une suite :
          mécontents, intéressés (bilan / réactivation), ou à vérifier.
        </p>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="filter-pending">À traiter</TabsTrigger>
          <TabsTrigger value="done" data-testid="filter-done">Traités</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="text-muted-foreground text-sm">Chargement...</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-4xl mb-2">🎉</div>
            <p className="text-muted-foreground">
              {filter === 'pending'
                ? 'Aucun client à rappeler — tout est à jour !'
                : 'Aucun rappel traité pour le moment.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop: table */}
          <div className="hidden md:block">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted">
                    <TableHead className="uppercase text-xs">Client</TableHead>
                    <TableHead className="uppercase text-xs">Besoin</TableHead>
                    <TableHead className="uppercase text-xs">Quand rappeler</TableHead>
                    <TableHead className="uppercase text-xs">Scénario</TableHead>
                    <TableHead className="uppercase text-xs">Date</TableHead>
                    <TableHead className="uppercase text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((cb) => (
                    <TableRow key={cb.id} data-testid={`callback-row-${cb.id}`}>
                      <TableCell>
                        <div className="font-medium">{cb.client_name || '—'}</div>
                        <ContactLinks cb={cb} />
                      </TableCell>
                      <TableCell><ReasonBadge reason={cb.reason} /></TableCell>
                      <TableCell><PreferenceTag pref={cb.preference_rappel} /></TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{scenarioLabel(cb.scenario)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock size={12} /> {formatDate(cb.received_at || cb.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right"><Actions cb={cb} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>

          {/* Mobile: cards */}
          <div className="md:hidden space-y-3">
            {items.map((cb) => (
              <Card key={cb.id}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{cb.client_name}</div>
                      <ContactLinks cb={cb} />
                    </div>
                    <ReasonBadge reason={cb.reason} />
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <PreferenceTag pref={cb.preference_rappel} />
                    <Badge variant="outline">{scenarioLabel(cb.scenario)}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(cb.received_at || cb.created_at)}
                    </span>
                  </div>
                  <Actions cb={cb} full />
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      <ActivityDetailDialog entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
    </motion.div>
  );
};

export default Callbacks;
